import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo
} from 'react'
import { useDispatch } from 'react-redux'

import { setLoading } from 'redux/store/common/slice'
import { TResponsibleUser } from 'features/RFP/RFPDetails/types'
import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import { useContractDetailsPopup } from 'features/ContractDetails/Providers/ContractDetailsPopupProvider'
import { updateContractResponsibleUsersRequestAsync } from '../../../../Reassign/api'
import { VALIDATION_MESSAGES } from '../../../../../constants'
import { ROLES } from '../../../../Permission'

type ContextProps = {
  actions: {
    deleteResponsibleUserAsync: (
      responsibleUserUuid: TResponsibleUser['uuid']
    ) => Promise<void>
    addResponsibleUserAsync: (
      responsibleUsers: Array<TResponsibleUser['uuid']>
    ) => Promise<void>
  }
}

const ContractResponsibleContext = createContext<ContextProps>({
  actions: {} as ContextProps['actions']
})

const ContractResponsibleContextProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const dispatch = useDispatch()

  const contractDetailsContext = useContractDetailsContext()
  const { deleteResponsibleUserPopup, addResponsibleUserPopup } =
    useContractDetailsPopup()

  const { details, isVendor, isCommons } = contractDetailsContext.state
  const responsibleUsers = useMemo(() => {
    if (isVendor) {
      return details.vendor_responsibles ?? []
    } else if (isCommons) {
      return details.commons_responsibles ?? []
    } else {
      return details.community_responsibles ?? []
    }
  }, [isVendor, details, isCommons])
  const { getContractDetailsAsync } = contractDetailsContext.actions

  const deleteResponsibleUserAsync = useCallback(
    async (responsibleUserUuid: TResponsibleUser['uuid']) => {
      if (!details.uuid) {
        throw new Error('Contract ID not provided')
      }
      const responsibles = responsibleUsers
        .filter(
          (u) =>
            u.uuid !== responsibleUserUuid &&
            u.role !== ROLES.VENDOR_CONTRACT_STEWARD
        )
        .map((u) => u.uuid)
      try {
        dispatch(setLoading(true))

        await updateContractResponsibleUsersRequestAsync(
          details.uuid,
          {
            responsibles
          },
          VALIDATION_MESSAGES.TEAM_MEMBER_USER_DELETED
        )

        await getContractDetailsAsync()

        deleteResponsibleUserPopup.actions.close()
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      deleteResponsibleUserPopup.actions,
      details,
      responsibleUsers,
      dispatch,
      getContractDetailsAsync
    ]
  )

  const addResponsibleUserAsync = useCallback(
    async (responsibles: Array<TResponsibleUser['uuid']>) => {
      if (!details.uuid) {
        throw new Error('Contract ID not provided')
      }

      try {
        dispatch(setLoading(true))

        await updateContractResponsibleUsersRequestAsync(
          details.uuid,
          {
            responsibles
          },
          VALIDATION_MESSAGES.TEAM_MEMBER_USER_ADDED
        )

        await getContractDetailsAsync()

        addResponsibleUserPopup.actions.close()
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [
      details.uuid,
      dispatch,
      getContractDetailsAsync,
      addResponsibleUserPopup.actions
    ]
  )

  const actions = useMemo(
    () => ({
      deleteResponsibleUserAsync,
      addResponsibleUserAsync
    }),
    [deleteResponsibleUserAsync, addResponsibleUserAsync]
  )

  return (
    <ContractResponsibleContext.Provider value={{ actions }}>
      {children}
    </ContractResponsibleContext.Provider>
  )
}

export const useContractResponsibleContext = () =>
  useContext(ContractResponsibleContext)

export default ContractResponsibleContextProvider
